import React, { useEffect, useState } from 'react';
import { Box, Toolbar, Typography, TextField, InputAdornment, Grid, Card, CardMedia, CardContent, Modal, Button, Stack, Divider, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';

const OutletOffers = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData'));
  const outletId = outletData?._id;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  const getOutletOffers = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API.BASE_URL}advertisement/user/new?outletId=${outletId}`, { headers });

      if (response?.data?.status) {
        const { clickableAdvertisements = [], nonClickableAdvertisements = [] } = response.data.data.advertisements;

        const allOffers = [...clickableAdvertisements, ...nonClickableAdvertisements]
          .filter(ad => !ad?.excludedOutlets?.includes(outletId))
          .map(ad => {
            const product = ad.product;
            const hasProduct = !!product;
            const mrp = hasProduct ? product.newMrp : 0;
            const offerPrice = hasProduct ? product.newOfferPrice : 0;
            const discount = mrp - offerPrice;
            const discountPercent = mrp > 0 ? Math.round((discount / mrp) * 100) : 0;

            return {
              id: ad._id,
              title: ad.title || product?.name || 'Untitled',
              image: ad.adImagePath ?? '',
              note: hasProduct ? `Now at ₹${offerPrice} (MRP ₹${mrp})` : '',
              product: hasProduct ? product : null,
              isTopImage: ad.isTopImage,
              isBottomImage: ad.isBottomImage,
              validityStartDate: ad.validityStartDate,
              validityEndDate: ad.validityEndDate,
              discount,
              discountPercent,
            };
          });

        setOffers(allOffers);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch offers", { position: "top-right", autoClose: 3000, theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOutletOffers();
  }, []);

  const isOfferActive = (offer) => {
    const start = offer.validityStartDate ? new Date(offer.validityStartDate) : null;
    const end = offer.validityEndDate ? new Date(offer.validityEndDate) : null;

    if (!start && !end) return false;

    // Normalize boundaries to include entire days
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    const now = new Date();

    if (start && end) return now >= start && now <= end;
    if (start) return now >= start; // from start onwards
    if (end) return now <= end;     // until end date
    return false;
  };

  const openModal = (offer) => {
    setSelectedOffer(offer);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedOffer(null);
    setIsModalOpen(false);
  };

  const visibleOffers = offers
    .filter(isOfferActive)
    .filter(offer => {
      const title = offer.title?.toLowerCase() || '';
      const note = offer.note?.toLowerCase() || '';
      return title.includes(debouncedSearch) || note.includes(debouncedSearch);
    });

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" align="center" sx={{ mb: 2, fontWeight: 600 }}>
        Running Offers
      </Typography>

      <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search offers"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" mt={2}>Loading offers...</Typography>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          {visibleOffers?.length > 0 ? (
            <Grid container spacing={3} justifyContent={'center'}>
              {visibleOffers?.map((offer) => (
                <Grid item xs={12} sm={6} md={4} key={offer.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'scale(1.02)' }
                    }}
                    onClick={() => openModal(offer)}
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={offer?.image}
                      alt={offer?.title}
                      sx={{
                        objectFit: 'contain',
                        backgroundColor: '#f5f5f5'
                      }}
                    />
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography variant="subtitle1" textTransform={'capitalize'} fontWeight={600}>{offer?.title}</Typography>
                        {offer?.note && (
                          <Typography variant="body2" color="text.secondary">
                            {offer?.note}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary" mt={2}>
                No active offers today.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Modal open={isModalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 480 },
            bgcolor: '#fff',
            borderRadius: 3,
            boxShadow: 6,
            p: 3,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {selectedOffer && (
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={700} color="#003049">
                {selectedOffer.title}
              </Typography>

              <Box
                component="img"
                src={selectedOffer?.image}
                alt={selectedOffer?.title}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  objectFit: 'contain',
                  maxHeight: 250,
                }}
              />

              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Type:</strong>{' '}
                  {selectedOffer?.product
                    ? 'Product Linked'
                    : selectedOffer?.isTopImage
                      ? 'Top Banner'
                      : selectedOffer?.isBottomImage
                        ? 'Bottom Banner'
                        : 'Standard'}
                </Typography>

                {(selectedOffer?.validityStartDate || selectedOffer?.validityEndDate) && (
                  <Typography variant="body2">
                    <strong>Valid:</strong>{' '}
                    {selectedOffer?.validityStartDate
                      ? new Date(selectedOffer?.validityStartDate).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'long', // "January", "February", etc.
                        year: 'numeric'
                      })
                      : 'N/A'}{' '}
                    -{' '}
                    {selectedOffer?.validityEndDate
                      ? new Date(selectedOffer?.validityEndDate).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                      : 'N/A'}
                  </Typography>
                )}
              </Stack>

              {selectedOffer?.product && (
                <>
                  <Divider />
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Product: {selectedOffer?.product?.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>MRP:</strong> ₹{selectedOffer?.product?.newMrp}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Offer Price:</strong> ₹{selectedOffer?.product?.newOfferPrice}
                    </Typography>
                    <Typography variant="body2" color="green">
                      <strong>You Save:</strong> ₹{selectedOffer?.discount} ({selectedOffer?.discountPercent}% OFF)
                    </Typography>
                  </Stack>
                </>
              )}

              <Button
                onClick={closeModal}
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  backgroundColor: '#003049',
                  '&:hover': {
                    backgroundColor: '#002037',
                  },
                }}
              >
                Close
              </Button>
            </Stack>
          )}
        </Box>
      </Modal>

    </Box>
  );
};

export default OutletOffers;
